import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { 
  TagIcon, 
  CubeIcon, 
  UsersIcon, 
  ChartBarIcon,
  PlusIcon,
  DocumentTextIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const ProductsDashboard = () => {
  const [stats, setStats] = useState({
    categories: 0,
    products: 0,
    activeProducts: 0,
    clientAssignments: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch counts from each endpoint
      const [categoriesRes, productsRes, usageRes] = await Promise.all([
        api.get('/products/categories'),
        api.get('/products'),
        api.get('/products/reports/usage')
      ]);

      const activeProducts = productsRes.data.filter(product => product.is_active).length;
      const totalAssignments = usageRes.data.reduce((sum, item) => sum + parseInt(item.client_count || 0), 0);

      setStats({
        categories: categoriesRes.data.length,
        products: productsRes.data.length,
        activeProducts,
        clientAssignments: totalAssignments
      });
    } catch (err) {
      console.error('Error fetching product stats:', err);
      setError('Failed to load product statistics');
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: 'Product Categories',
      count: stats.categories,
      icon: TagIcon,
      color: 'bg-blue-500',
      link: '/products/categories',
      description: 'Organize products by category'
    },
    {
      title: 'Total Products',
      count: stats.products,
      icon: CubeIcon,
      color: 'bg-green-500',
      link: '/products/list',
      description: 'All available products'
    },
    {
      title: 'Active Products',
      count: stats.activeProducts,
      icon: BuildingOfficeIcon,
      color: 'bg-purple-500',
      link: '/products/list?filter=active',
      description: 'Currently active products'
    },
    {
      title: 'Client Assignments',
      count: stats.clientAssignments,
      icon: UsersIcon,
      color: 'bg-orange-500',
      link: '/products/reports',
      description: 'Products assigned to clients'
    }
  ];

  const quickActions = [
    {
      title: 'Add New Product',
      description: 'Create a new product',
      icon: PlusIcon,
      link: '/products/add',
      color: 'bg-indigo-600 hover:bg-indigo-700'
    },
    {
      title: 'Add Category',
      description: 'Create a new product category',
      icon: TagIcon,
      link: '/products/categories/add',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: 'Usage Reports',
      description: 'View product usage analytics',
      icon: ChartBarIcon,
      link: '/products/reports',
      color: 'bg-green-600 hover:bg-green-700'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Product Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your product catalog and track client implementations
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="mt-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {dashboardCards.map((card) => (
            <Link
              key={card.title}
              to={card.link}
              className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <dt>
                <div className={`absolute ${card.color} rounded-md p-3`}>
                  <card.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 text-sm font-medium text-gray-500 truncate">{card.title}</p>
              </dt>
              <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
                <p className="text-2xl font-semibold text-gray-900">{card.count}</p>
                <div className="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6">
                  <div className="text-sm">
                    <span className="text-gray-600">{card.description}</span>
                  </div>
                </div>
              </dd>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action) => (
            <Link
              key={action.title}
              to={action.link}
              className={`relative group ${action.color} p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg text-white transition-colors duration-200`}
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-white bg-opacity-20">
                  <action.icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  {action.title}
                </h3>
                <p className="mt-2 text-sm text-white text-opacity-90">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity or Summary */}
      <div className="mt-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">System Overview</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {stats.activeProducts} of {stats.products} products are currently active
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Assigned to clients across {stats.clientAssignments} implementations
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsDashboard; 